import {useEffect, useState, useCallback} from 'react';
import {useStore} from '@/store/root-store';
import {
  Drawer,
  Progress,
  Tooltip, Select} from 'antd';
import {observer} from 'mobx-react';
import styles from './style.module.scss';
import styled from 'styled-components';
import {formatNumber, numberWithCommas} from '@/utils/number';
import {Table, BarChartColumn, SkeletonRingLoader, Pagination, SkeletonHorizontalLoaderGray, Button} from '@/components/common-components';
import ReactEcharts from 'echarts-for-react';
import {EChartOption} from 'echarts';
import moment from 'moment';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {getProgressColor} from '@/utils/colors';
import {faArrowUp} from '@fortawesome/pro-regular-svg-icons';
import {faCaretDown} from '@fortawesome/pro-solid-svg-icons';
import {toJS} from 'mobx';
import numeral from 'numeral';
import Flag from '@/components/common-components/components/flag';
import {SimilarKeyword} from './SimilarKeyword';
import {CloseOutlined} from '@ant-design/icons';
import {NextImg} from '@/utils/nextImg';

export const ContentResearchDrawer = observer(() => {
  const [isHovered, setIsHovered] = useState(false);


  const {
    countries: {getSpecificCountryByCode},
    OverviewPage: {
      isContentResearchDrawerOpen,
      loading,
      keywordDetail,
      closeContentResearchDrawer,
      getKeywordDetail,
      removeFromLoadingArray,
    },
    contentOptimizer: {currentPage},
  } = useStore('');
  const countryName = getSpecificCountryByCode(keywordDetail?.countryCode);

  const [activeKw, setActiveKw] = useState<string>('');
  const [similarKeywordsPageNumber, setSimilarKeywordsPageNumber] = useState<number>(1);
  const [similarKeywordsPageSize] = useState<number>(8);
  const [similarKeywordsData, setSimilarKeywordsData] = useState([]);
  const [similarKeywordsList, setSimilarKeywordsList] = useState([]);

  const [topCompetitorsData, setTopCompetitorsData] = useState([]);
  const [rankingPotentialData, setRankingPotentialData] = useState([]);

  const {Option} = Select;


  const handleOnSelectChange = value => {
    setActiveKw(value);
    getKeywordDetail(currentPage?.content?.targetKeywords?.filter(item => item.keyword === value)[0]?.id);
  };


  /* SIMILAR KEYWORDS TABLE UPDATE START */
  const handleFilterRenderData = useCallback((dataArray: any, pageNumber: number, pageSize: number) => {
    const targetKwArray = currentPage?.content?.targetKeywords && currentPage?.content?.targetKeywords.length ? toJS(currentPage.content.targetKeywords).map(item => {
      return JSON.stringify({
        keyword: item.keyword,
        countryCode: item.countryCode,
        locationId: item.locationId,
        location: item.location,
      });
    }) : [];

    let similarKeywordsFormattedData = [];
    if (dataArray.length && targetKwArray?.length ) {
      const updatedData = toJS(dataArray).map(item => {
        return {
          ...item,
          isKwIncluded: targetKwArray.includes(JSON.stringify({
            keyword: item.keyword,
            countryCode: currentPage.content.countryCode,
            locationId: currentPage.content.locationId,
            location: currentPage.content.location,
          })),
        };
      });


      similarKeywordsFormattedData = updatedData;
    } else {
      similarKeywordsFormattedData = dataArray;
    }
    setSimilarKeywordsData(similarKeywordsFormattedData.slice((pageNumber - 1) * pageSize, ((pageNumber - 1) * pageSize) + pageSize));
  }, []);

  useEffect(() => {
    if (keywordDetail?.similarKeywords?.length > 0) {
      const kwArray = [...(currentPage?.content?.targetKeywords ?? [])].map(kw => kw.keyword);
      removeFromLoadingArray(kwArray);
      handleFilterRenderData([...keywordDetail.similarKeywords], similarKeywordsPageNumber, similarKeywordsPageSize);
    }
  }, [[...(currentPage?.content?.targetKeywords ?? [])].length, activeKw, toJS(keywordDetail?.similarKeywords)?.length, handleFilterRenderData, keywordDetail?.similarKeywords, similarKeywordsPageNumber, similarKeywordsPageSize]);

  useEffect(() => {
    if (keywordDetail?.similarKeywords?.length > 0) {
      setSimilarKeywordsList(keywordDetail.similarKeywords);
    }
  }, [toJS(keywordDetail?.similarKeywords)?.length, toJS(keywordDetail?.similarKeywords), activeKw]);


  const onPaginationChange = async (pageNumber: number, pageSize: number) => {
    setSimilarKeywordsPageNumber(pageNumber);
    handleFilterRenderData(keywordDetail.similarKeywords, pageNumber, pageSize);
  };

  const handleIssueTableChange = (pagination, filters, sorter) => {
    const {column, order} = sorter;
    const {sortField} = column || {};
    const data = [...similarKeywordsList];

    if (sortField && order && data) {
      data.sort( (a, b) => {
        const _dir = order === 'ascend' ? 1 : -1;
        if (a[sortField] > b[sortField]) return 1 * _dir;
        if (a[sortField] < b[sortField]) return -1 * _dir;
        return 0;
      });
      handleFilterRenderData(data, similarKeywordsPageNumber, similarKeywordsPageSize);
    } else {
      handleFilterRenderData(keywordDetail.similarKeywords, similarKeywordsPageNumber, similarKeywordsPageSize);
    }
  };
  /* SIMILAR KEYWORDS TABLE UPDATE END */

  /* TOP COMPETITORS TABLE UPDATE START */
  useEffect(() => {
    if (keywordDetail?.competitors?.length > 0) {
      setTopCompetitorsData(keywordDetail.competitors);
    }
  }, [toJS(keywordDetail?.competitors)?.length, activeKw]);


  const handleTopCompetitorsTableChange = (pagination, filters, sorter) => {
    const {column, order} = sorter;
    const {sortField} = column || {};
    const data = [...topCompetitorsData];
    if (sortField && order && data) {
      data.sort( (a, b) => {
        const _dir = order === 'ascend' ? 1 : -1;
        if ((a[sortField] || 0) > (b[sortField] || 0)) return 1 * _dir;
        if ((a[sortField] || 0) < (b[sortField] || 0)) return -1 * _dir;
        return 0;
      });

      setTopCompetitorsData(data);
    } else {
      setTopCompetitorsData(keywordDetail?.competitors);
    }
  };
  /* TOP COMPETITORS TABLE UPDATE END */

  /* RANKING POTENTIAL TABLE UPDATE START */
  useEffect(() => {
    const relevanceData = [];
    relevanceData?.push({
      competition: keywordDetail?.typicalTop10 && Object.keys(keywordDetail?.typicalTop10)[0],
      contentScore: keywordDetail?.typicalTop10?.typicalTop10?.averageContentScores,
      dr: keywordDetail?.typicalTop10?.typicalTop10?.averageDr,
      wordCount: keywordDetail?.typicalTop10?.typicalTop10?.averageWordCount,
      readAbility: keywordDetail?.typicalTop10?.typicalTop10?.averageReadability,
    });

    if (relevanceData.length > 0) {
      setRankingPotentialData(relevanceData);
    }
  }, [
    activeKw, keywordDetail?.typicalTop10?.typicalTop10?.averageContentScores,
    keywordDetail?.typicalTop10?.typicalTop10?.averageDr,
    keywordDetail?.typicalTop10?.typicalTop10?.averageWordCount,
    keywordDetail?.typicalTop10?.typicalTop10?.averageReadability,
  ]);

  // const addDefaultSrc = e => {
  //   e.target.src = '/img/world-wide-web.svg';
  // };


  const handleRankingPotentialTableChange = (pagination, filters, sorter) => {
    const {column, order} = sorter;
    const {sortField} = column || {};
    const data = [...rankingPotentialData];
    if (sortField && order && data) {
      data.sort( (a, b) => {
        const _dir = order === 'ascend' ? 1 : -1;
        if ((a[sortField] || 0) > (b[sortField] || 0)) return 1 * _dir;
        if ((a[sortField] || 0) < (b[sortField] || 0)) return -1 * _dir;
        return 0;
      });

      setRankingPotentialData(data);
    } else {
      setRankingPotentialData(keywordDetail?.competitors);
    }
  };
  /* RANKING POTENTIAL TABLE UPDATE END */

  const columns = [
    {
      title: 'AVERAGE PER COMPETION',
      dataIndex: 'competition',
      key: 'competition',
      width: 180,
      align: 'left' as 'left',
      render: (_, data) => <div className={styles.firstColumnText}>{data?.competition || '-'}</div>,
    },
    {
      title: 'CONTENT SCORE',
      dataIndex: 'contentScore',
      sorter: true,
      sortField: 'contentScore',
      width: 100,
      align: 'center' as 'center',
      key: 'contentScore',
      render: (_, data) => {
        const percent = data?.contentScore ? data?.contentScore % 1 === 0 ? data?.contentScore : parseFloat(data?.contentScore.toFixed(2)) : 0;
        return <div>
          <Progress
            percent={percent}
            strokeLinecap='round'
            gapDegree={0}
            trailColor='#ddd'
            strokeColor={'#2AC155'}
            showInfo={true}
            type='circle'
            width={30}
            strokeWidth={8}
            size='small'
            format={percent => <span style={{fontSize: '14px'}}>{percent}</span>}
            className={styles.progress}
          />
        </div>;
      },
    },
    {
      title: 'DR',
      dataIndex: 'dr',
      sorter: true,
      sortField: 'dr',
      width: 80,
      align: 'center' as 'center',
      key: 'dr',
      render: (_, data) => {
        const percent = data?.dr ? data?.dr % 1 === 0 ? data?.dr : parseFloat(data?.dr.toFixed(2)) : 0;
        return <div>
          <Progress
            percent={percent}
            strokeLinecap='round'
            gapDegree={0}
            trailColor='#ddd'
            strokeColor={'#2AC155'}
            showInfo={true}
            type='circle'
            width={30}
            strokeWidth={8}
            size='small'
            format={percent => <span style={{fontSize: '14px'}}>{percent}</span>}
            className={styles.progress}
          />
        </div>;
      },
    },
    {
      title: 'WORD COUNT',
      key: 'wordCount',
      sortField: 'wordCount',
      sorter: true,
      width: 110,
      align: 'center' as 'center',
      dataIndex: 'wordCount',
      render: (_, data) => {
        return <div className={styles.firstColumnText} style={{justifyContent: 'center'}}>{data?.wordCount ? numberWithCommas(data?.wordCount) : '-'}</div>;
      },


    },
    {
      title: 'READABILITY',
      key: 'readAbility',
      dataIndex: 'readAbility',
      align: 'center' as 'center',
      width: 110,
      render: (_, data) => {
        return <div className={styles.firstColumnText} style={{justifyContent: 'center'}}>{data?.readAbility ? data?.readAbility : '-'}</div>;
      },

    },
  ];

  const topCompetitorColumns = [
    {
      title: 'SITE / URL',
      dataIndex: 'hostname',
      key: 'hostname',
      width: 300,
      align: 'left' as 'left',
      fixed: 'left' as 'left',
      render: (_, data) => {
        return <div style={{display: 'flex'}}>
          <NextImg className={styles.competitorImg} src={`https://www.google.com/s2/favicons?sz=16&domain_url=${data?.url}`} />
          <div>
            <div className={styles.titleText}>{data.hostname}</div>
            <a target='_blank' rel='noreferrer' href={data?.url}><div className={styles.urlText}>{data.url}</div></a>
          </div>
        </div>;
      },

    },
    {
      title: 'POS',
      dataIndex: 'position',
      key: 'position',
      width: 50,
      sortField: 'position',
      align: 'left' as 'left',
      render: (_, data) => {
        return <div style={{textAlign: 'center'}}>{data?.position}</div>;
      },
    },
    {
      title: ()=> <div style={{textAlign: 'center'}}><span>CONTENT</span><span style={{display: 'block'}}>SCORE</span></div>,
      dataIndex: 'contentScore',
      sortField: 'contentScore',
      sorter: true,
      width: 130,
      align: 'center' as 'center',
      key: 'contentScore',
      render: (_, data) => {
        const percent = data?.contentScore ? data?.contentScore % 1 === 0 ? data?.contentScore : parseFloat(data?.contentScore.toFixed(2)) : 0;
        return <div>
          <Progress
            percent={percent}
            strokeLinecap='round'
            gapDegree={0}
            trailColor='#ddd'
            strokeColor={'#2AC155'}
            showInfo={true}
            type='circle'
            width={30}
            strokeWidth={8}
            size='small'
            format={percent => <span style={{color: '#000', fontSize: '14px'}}>{percent}</span>}
            className={styles.progress}
          />
        </div>;
      },
    },
    {
      title: 'DR',
      dataIndex: 'dr',
      sortField: 'dr',
      sorter: true,
      width: 60,
      align: 'center' as 'center',
      key: 'dr',
      render: (_, data) => {
        const percent = data?.dr ? data?.dr % 1 === 0 ? data?.dr : parseFloat(data?.dr.toFixed(2)) : 0;
        return <div>
          <Progress
            percent={percent}
            strokeLinecap='round'
            gapDegree={0}
            trailColor='#ddd'
            strokeColor={'#2AC155'}
            showInfo={true}
            type='circle'
            width={30}
            strokeWidth={8}
            size='small'
            format={percent => <span style={{color: '#000', fontSize: '14px'}}>{percent}</span>}
            className={styles.progress}
          />
        </div>;
      },
    },
    {
      title: 'WORD COUNT',
      key: 'wordCount',
      sortField: 'wordCount',
      sorter: true,
      align: 'center' as 'center',
      width: 100,
      dataIndex: 'wordCount',
      render: (_, data) => {
        return <div className={styles.firstColumnText} style={{textAlign: 'center'}}>{data?.wordCount ? numberWithCommas(data?.wordCount) : '-'}</div>;
      },


    },
    {
      title: 'READABILITY',
      key: 'readability',
      dataIndex: 'readability',
      align: 'center' as 'center',
      width: 170,
      render: (_, data) => {
        return <div className={styles.firstColumnText} style={{marginRight: 8}}>{data?.readability ? data?.readability : '-'}</div>;
      },

    },
  ];

  const squareTooltip = (hostname, url, keyword, score) => (
    <div style={{display: 'flex'}}>
      <div style={{marginRight: 10, marginTop: 7}}><NextImg style={{width: 30, height: 30}} src={`https://www.google.com/s2/favicons?sz=16&domain_url=${hostname}`} /></div>
      <div>
        <div className={styles.truncate} style={{fontWeight: 500}}>{hostname}</div>
        <a className={styles.truncate} href={url} style={{fontSize: '12px'}}>{url}</a>
        <div style={{fontSize: '12px', marginTop: 5}}>
          <span>{score > 0 ? `uses ` : `did not use `}</span>
          <span style={{textDecoration: 'underline'}}>{keyword}</span>&nbsp;
          {score > 0 && <><IsRanking>{score}</IsRanking>
            <span>{score == 1 ? `time` : `times`}</span></>}
        </div>
      </div>
    </div>
  );

  const SimilarKeywordConfig = [
    {
      title: 'KEYWORD',
      dataIndex: 'keyword',
      key: 'keyword',
      align: 'left' as 'left',
      width: 240,
      render: (_, record) => {
        return (<SimilarKeyword keyword={record.keyword} isIncluded={record.isKwIncluded}/>);
      },
    },
    {
      title: 'TREND',
      dataIndex: 'histSv',
      align: 'center' as 'center',
      key: 'histSv',
      width: 70,
      render: (series: EChartOption.Series[]) => {
        const formatSeries = {
          data: series,
          itemStyle: {color: '#7f4ead'},
          type: 'bar',
        };
        return <div style={{display: 'flex', justifyContent: 'center'}}><BarChartColumn series={formatSeries} /></div>;
      },
    },

    {
      title: 'SV',
      width: 70,
      dataIndex: 'sv',
      align: 'center' as 'center',
      key: 'sv',
      sortField: 'sv',
      sorter: true,
      render: (_, data) => {
        return <div>{numeral(data?.sv).format('0.[0][a]')}</div>;
      },
    },

    {
      title: 'CPC',
      dataIndex: 'cpc',
      align: 'center' as 'center',
      width: 70,
      key: 'cpc',
      sortField: 'cpc',
      sorter: true,
      render: (_, data) => {
        return <div>{`$${data?.cpc}`}</div>;
      },
    },
  ];
  // useEffect(() => {
  //   if (isContentResearchDrawerOpen && !isInitialDataFetch) {
  //     getKeywordDetail(currentPage?.content?.targetKeywords?.filter(item => item.keyword === activeKw)[0]?.id);
  //   }
  // }, [activeKw]);

  return <Drawer
    visible={isContentResearchDrawerOpen}
    placement='right'
    width={'80vw'}

    onClose={() => {
      closeContentResearchDrawer();
    }}
    closeIcon={<CloseOutlined style={isContentResearchDrawerOpen ? {display: 'block'} : {display: 'none'}}/>}
    className={styles.contentResearchDrawerStyle}
    afterVisibleChange={visible => {
      if (visible) {
        setActiveKw(currentPage?.content?.targetKeywords[0]?.keyword || '');
        getKeywordDetail(currentPage?.content?.targetKeywords[0]?.id);
      }
    }}
    bodyStyle={{backgroundColor: '#F2F2F5', paddingLeft: '0', paddingRight: '0'}}>
    <PageTitle>
        Content Research
    </PageTitle>
    <Header>
      <span style={{marginTop: 'auto', marginBottom: 'auto', marginRight: '15px'}}>
              Based on Keyword:
      </span>
      <Select
        // disabled={!isSubscriber}
        value={`${activeKw}`}
        defaultValue={activeKw}
        style={{width: '230px'}}
        onSelect={value => handleOnSelectChange(value)}
        dropdownStyle={{minWidth: '150px', fontSize: '12px'}}
        dropdownClassName={styles.keywordDropdownList}
        className={styles.selectInput}
        suffixIcon={<FontAwesomeIcon icon={faCaretDown} style={{color: '#000'}}/>}
      >
        {currentPage?.content?.targetKeywords?.map(item => {
          return <Option key={item.keyword} value={item.keyword}>{item.keyword}</Option>;
        })}

      </Select>
    </Header>
    <div className={styles.topTagsContainer}>
      <div style={{display: 'flex', marginTop: '12px'}}>

        {loading ? <SkeletonHorizontalLoaderGray lightLoader={true} skeletonMarginRight={'10px'} height={30}/> :
          <div className={styles.topTags}>{keywordDetail?.countryCode ? <Flag style={{marginBottom: '4px', width: '24px'}} code={keywordDetail?.countryCode} /> : <NextImg style={{marginTop: '-2px', width: '15px'}} src='/img/world-wide-web.svg'/>}
            <span style={{marginLeft: 7}}>{keywordDetail?.countryCode ? countryName?.name : 'Worldwide'}</span>
          </div>}

        {loading ? <SkeletonHorizontalLoaderGray lightLoader={true} skeletonMarginRight={'10px'} height={30}/> :
          <div className={styles.topTags}>
                KD: <span style={{marginRight: '5px', color: keywordDetail?.kd ? keywordDetail?.kd > 75 ? '#F44343' : keywordDetail?.kd < 76 && keywordDetail?.kd > 50 ? '#e4a469' : keywordDetail?.kd < 51 && keywordDetail?.kd > 25 ? '#bfe15e' : '#1d754e' : '#696777'}}>•</span>{keywordDetail?.kd || '-'}
          </div>}
        {loading ? <SkeletonHorizontalLoaderGray lightLoader={true} skeletonMarginRight={'10px'} height={30}/> : <div className={styles.topTags}>SV: {numeral(keywordDetail?.sv).format('0.[0][a]')}</div>}

        {loading ? <SkeletonHorizontalLoaderGray lightLoader={true} skeletonMarginRight={'10px'} height={30}/> :
          <div className={styles.topTags}>Total Search Results: {keywordDetail?.totalSearchResults ? numeral(keywordDetail?.totalSearchResults).format('0.[0][a]') : '0'}</div>}
      </div>
    </div>
    <div className={styles.overViewContainer}>
      <div className={styles.rankingContainer}>
        <div className={styles.title}>Ranking Potential</div>
        {keywordDetail?.rankingPotential ?
          <div style={{display: 'flex', alignItems: 'center'}}>
            <div className={styles.dot}></div>
            <div className={styles.boldTitle}>{keywordDetail?.rankingPotential}</div>
          </div> : null }
        <Table
          className={styles.rankingTable}
          columns={columns}
          dataSource={toJS(rankingPotentialData)}
          pagination={false}
          loadingRows={1}
          loading={loading}
          onChange={handleRankingPotentialTableChange}
        />
      </div>
      <div style={{width: '40%'}}>
        <div className={styles.searchVolume}>
          <div className={styles.title}>Search Volume</div>
          {loading ? <SkeletonRingLoader/> : <><div className={styles.boldTitle} style={{marginBottom: '40px'}}>{formatNumber(keywordDetail?.sv)}</div>
            <div>
              <ReactEcharts
                style={{width: '100%', height: '110px'}}
                option={{
                  color: '#7F4EAD',
                  xAxis: {
                    data: keywordDetail?.svTrend ? keywordDetail?.svTrend?.map(data => data.date) : [],
                    type: 'category',
                    show: true,
                    axisLabel: {
                      interval: 10,
                      formatter: label => {
                        const date = moment(label);
                        return `{month|${date.format('MMM')}}-{year|${date.format('YY')}}`;
                      },
                      rich: {
                        month: {
                          color: '#77757D',
                          lineHeight: 15.54,
                        },
                        year: {
                          color: '#77757D',
                          fontSize: 12,
                          lineHeight: 12.95,
                        },
                      },
                    },
                    splitLine: {
                      show: false,
                    },
                  },
                  yAxis: {
                    type: 'value',
                    show: true,
                    axisLabel: {
                      formatter: label => {
                        return `${formatNumber(label)}`;
                      },
                    },
                    splitLine: {
                      lineStyle: {
                        color: '#E8E8E8',
                        type: 'dashed',
                        width: 1},
                      show: true,
                    },
                  },
                  grid: {
                    height: '70%',
                    left: '10%',
                    top: '9%',
                  },
                  series: [
                    {
                      data: keywordDetail?.svTrend?.map(data => data.volume) || [],
                      type: 'line',
                      areaStyle: {},
                      symbolSize: 2,
                      symbol: '',
                    },
                  ],
                }} />
            </div></>}

        </div>
      </div>


    </div>
    <div className={styles.commonCitations}>
      <div className={styles.title}>Common Citations</div>
      {loading ? <SkeletonRingLoader height={'70px'}/> :
        <div className={styles.citationScroll}>
          {keywordDetail?.commonCitations?.length ? keywordDetail?.commonCitations?.map((citation, index) => {
            return (citation?.url && !citation?.url.startsWith('javascript:')) && <div key={index} className={styles.singleCitation}>
              <div className={styles.citationIcon}>{citation.url && <NextImg
              // onError={e => addDefaultSrc(e)}
                src={`https://www.google.com/s2/favicons?sz=16&domain_url=${citation.url}`} />}</div>
              <div className={styles.citationInfo}>
                <div className={styles.citationName}>{citation?.title}</div>

                {citation?.url ?
                  <a target='_blank' rel='noreferrer' href={citation?.url} className={styles.citationUrl}>{citation?.url}</a> : null}
                {citation?.metaDesc && citation?.metaDesc?.length > 70 ?
                  <Tooltip
                    placement='left'
                    title={citation?.metaDesc}
                    overlayClassName={styles.metaDescTooltip}>
                    <div className={styles.citationText}>{citation?.metaDesc.substring(0, 70) + '...'}</div>
                  </Tooltip> : <div className={styles.citationText}>{citation?.metaDesc}</div>}
                {citation?.serps ? <div className={styles.serpUsedByContainer}>
                  <span style={{fontSize: '10px', marginTop: '5px', marginBottom: 'auto', marginRight: '6px'}}>Used by:</span>
                  <div className={styles.citationSerpsContainer}>
                    {citation?.serps?.map((serp, idx) => idx < 5 && (
                      <Tooltip
                        key={idx}
                        title={<>
                          <div className={styles.serpTitle}>{serp.title}</div>
                          {serp?.url ? <a target='_blank' rel='noreferrer' href={serp.url} className={styles.serpUrl}>{serp.url}</a> : null}
                          <div className={styles.citationSerpMetaDesc}>{serp.metaDesc && serp?.metaDesc?.length > 70 ? serp?.metaDesc?.substring(0, 70) + '...' : serp.metaDesc}</div>
                          <div className={styles.citationSerPosition}>{`Position: ${serp.position}`}</div>
                        </>}
                        overlayClassName={styles.serpTooltip}>
                        <div className={styles.serpIcon}><NextImg
                        // onError={e => addDefaultSrc(e)}
                          src={`https://www.google.com/s2/favicons?sz=16&domain_url=${serp.url}`} /></div>
                      </Tooltip>
                    ))}
                    {citation?.serps?.length > 9 && (
                      <span>{`+${citation?.serps?.length - 9}`}</span>
                    )}
                  </div>
                </div> : null}

              </div>
            </div>;
          }) : <div style={{margin: '35px auto'}}>No data available</div>}
          {/* return <div className={styles.citation} key={index}>
              <NextImg src={`https://www.google.com/s2/favicons?sz=16&domain_url=${sanitizeUrl(item?.url)}`} />
              <div className={styles.citationContent}>

                <div className={styles.contentTitle}>{getDomain(item?.url)}</div>
                <div className={styles.contentDescription}>
                  {item?.title}
                </div>
              </div>
            </div>;
          })} */}
        </div>}
    </div>
    <MiddleContainer>
      <div style={{width: '58%', marginRight: '20px'}}>
        <TitleContainer>
          <Title>Top competitor content</Title>

        </TitleContainer>
        <Table
          className={styles.topCompetitorTable}
          columns={topCompetitorColumns}
          dataSource={toJS(topCompetitorsData)}
          pagination={false}
          onChange={handleTopCompetitorsTableChange}
          loading={loading}
        />
      </div>
      <div style={{width: '42%'}}>
        <TitleContainer>
          <Title>Similar keywords</Title>

        </TitleContainer>
        <Table
          className={styles.similarKeywordsTable}
          columns={SimilarKeywordConfig}
          dataSource={toJS(similarKeywordsData)}
          onChange={handleIssueTableChange}
          loading={loading}
          pagination={false}
        />
        <div className={styles.paginationContainer}>
          <Pagination
            onChange={onPaginationChange}
            total={keywordDetail?.similarKeywords?.length}
            pageSize={similarKeywordsPageSize}
            current={similarKeywordsPageNumber}
            showSizeChanger={false}
            defaultPageSize={8}
            showQuickJumper={false}
          />
        </div>
        <Button
          buttonType='transparent'
          className={styles.viewAllKWButton}
          onClick={() => {
            closeContentResearchDrawer();
            currentPage.setIsOpeningKwSuggestionFromContentResearch(true);
            currentPage.openCompetingFocusTermsDrawer();
          }}>View all keywords</Button>
      </div>

    </MiddleContainer>
    <div style={{backgroundColor: 'rgb(242 242 245)', padding: '0 40px 40px 40px'}}>

      <div style={{backgroundColor: '#fff'}}>
        <TitleContainer>
          <Title>Relevant terms used by Competitors</Title>

        </TitleContainer>
        {loading ? <SkeletonRingLoader height={'300px'}/> : <>
          <SectionHeader>
            <span style={{marginRight: 125}}>COMPETITOR RANK</span>
            <div style={{display: 'flex', gap: 2, alignItems: 'center'}}>
              {keywordDetail && keywordDetail?.termsGrid?.sites?.map((x, i) => (
                <Rank key={i}>{i + 1}</Rank>
              ))}
            </div>
          </SectionHeader>
          <div style={{alignItems: 'end', textAlign: 'center', marginTop: 10, marginBottom: 20, marginLeft: 252, display: 'flex', gap: 2}}>
            {keywordDetail && keywordDetail?.termsGrid?.sites?.map((x, i) => {
              if (!x?.hostname) {
                return (
                  <Site key={`site-${x?.url}-${i}`} style={{color: '#121212'}}>
                        Created From Scratch
                  </Site>
                );
              }
              return (
                <a href={`${x?.url}`} target='_blank' rel='noreferrer' key={`site-${x?.url}-${i}`}>
                  <Site>
                    {/* {x?.hostname} */}
                  </Site>
                  <NextImg src={`https://www.google.com/s2/favicons?sz=16&domain_url=${x.hostname}`} />
                </a>
              );
            })}
          </div>


          <SectionHeader style={{marginBottom: 12, justifyContent: 'space-between'}}>
            <div>
              <span style={{marginRight: 39}}>FOCUS TERMS</span>
              <span>DISTRIBUTION</span>
              <span style={{marginLeft: 7}}><FontAwesomeIcon transform={{rotate: 140}} icon={faArrowUp} color='#A3A4A4' /></span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', fontWeight: 400}}>
              <Dot style={{backgroundColor: '#F44343'}}></Dot><span style={{marginRight: 14}}>0</span>
              <Dot style={{backgroundColor: '#FF8536'}}></Dot><span style={{marginRight: 14}}>1-3</span>
              <Dot style={{backgroundColor: '#BFE15E'}}></Dot><span style={{marginRight: 14}}>3-10</span>
              <Dot style={{backgroundColor: '#2AC155'}}></Dot><span style={{marginRight: 14}}>10+</span>
            </div>
          </SectionHeader>
          {keywordDetail && keywordDetail?.termsGrid?.focusTermUsageBySites?.map((item, i) => (
            <div style={{display: 'flex', gap: 2, marginTop: 2}} key={`square-${item.name}-${i}`}>
              <div style={{width: 241, marginLeft: 10, color: '#2D6CCA'}}>{item.name}</div>
              {item.values?.map((score, i) => (
                <Tooltip overlayClassName={styles.tooltipOverlay} key={`${item.name}-score-${i}`} title={
                  squareTooltip(keywordDetail?.termsGrid?.sites?.[i].hostname, keywordDetail?.termsGrid?.sites?.[i].url, item.name, score)
                }>
                  <Square key={i} isHovered={isHovered} onMouseEnter={() => {
                    setIsHovered(true);
                  }} onMouseLeave={() => {
                    setIsHovered(false);
                  }} style={{backgroundColor: getProgressColor(score ?? 0, 0, 3, 10)}}>{score}</Square>
                </Tooltip>
              ))}
            </div>
          ))}</>}
      </div>
    </div>
  </Drawer>;
});

const PageTitle = styled.div`
  font-weight: 600;
  margin-bottom: 10px;
  margin-left: 40px;
  font-size: 20px;
`;
const Header = styled.div`
  display: flex;
  margin-bottom: 10px;
  padding-left: 40px;
`;

const TitleContainer = styled.div`
height: 60px;
background: #2D2F34;
padding: 18px;
border-radius: 8px 8px 0px 0px;
`;
const Title = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: #fff;
  line-height: 22px;
`;

const SectionHeader = styled.div`
  background: #F7F7F8;
  border-radius: 3px;
  margin-right: 4px;
  margin-left: 4px;
  display: flex;
  color: #121212;
  padding: 15px 10px;
  font-size: 12px;
  font-weight: 600;
`;

const MiddleContainer = styled.div`
  display: flex;
  padding: 0 40px 30px 40px;
`;

const Square = styled.div<{ isHovered: boolean }>`
  width: 40px;
  height: 25px;
  border-radius: 3px;
  background-color: red;
  cursor: pointer;
  color: transparent;
  
  opacity: ${p => p.isHovered && '0.5'};
  
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: white;
    opacity: 1;
  }
`;

const Dot = styled.div`
  border-radius: 50%;
  height: 5px;
  margin-right: 5px;
  width: 5px;
`;

const Rank = styled.div`
  background-color: #d3d3d7;
  width: 40px;
  height: 25px;
  font-size: 12px;
  color: #4E5156;
  justify-content: center;
  display: flex;
  align-items: center;
`;


const Site = styled.div`
  writing-mode: tb-rl;
  transform: rotate(-180deg);
  font-size: 12px;
  color: #2D6CCA;
  width: 40px;
  display: flex;
  align-items: center;
`;

const IsRanking = styled.span`
  padding: 2px 7px;
  background-color: #1FAC47;
  border-radius: 3px;
  color: #fff;
  margin-right: 5px;
`;
